import { useMemo, useState } from 'react'

import { useAuthContext } from 'context/AuthContext'
import { useActiveMerchant } from 'lib/merchant-group'
import { useRouter } from 'next/router'

type UseFormCompanyParams = {
  // Edição e duplicação já nascem na empresa do registro (o modal de detalhe
  // registra a empresa ativa): o seletor só vale para criação.
  enabled?: boolean
}

export type FormCompany = {
  // undefined fora da visão multi-empresas (ou com o seletor desligado)
  companyUuid?: string
  setCompanyUuid: (merchantUuid: string) => void
}

/**
 * SC-10999: empresa de um formulário de criação na visão multi-empresas.
 *
 * Pré-seleciona a empresa filtrada na lista (quando o filtro "Empresa" tem
 * exatamente uma) e, sem isso, a empresa padrão do grupo. Enquanto o
 * formulário estiver montado, a empresa escolhida é a empresa ativa: toda
 * chamada sem escopo explícito (autocompletes, criação inline de cliente ou
 * produto, submit) vai com o token dela.
 *
 * Deve ser usado no componente que envolve o formulário, e não dentro dele:
 * o registro acontece em layout effect, antes das buscas dos filhos.
 */
// Os testes dos formulários montam sem o router do Next, e o `useRouter` do
// Next 14 lança erro nesse caso em vez de devolver null. Sem router, não há
// filtro de empresa na URL para pré-selecionar.
const useOptionalRouterQuery = () => {
  try {
    return useRouter()?.query
  } catch {
    return undefined
  }
}

const useFormCompany = ({
  enabled = true
}: UseFormCompanyParams = {}): FormCompany => {
  const { groupSession } = useAuthContext()
  const query = useOptionalRouterQuery()

  const initialCompany = useMemo(() => {
    if (!enabled || !groupSession) return undefined

    const merchantUuids = groupSession.merchants.map(
      ({ merchant_uuid }) => merchant_uuid
    )
    const filtered = String(query?.merchant_uuids || '')
      .split('|')
      .filter(Boolean)

    if (filtered.length === 1 && merchantUuids.includes(filtered[0])) {
      return filtered[0]
    }

    if (
      groupSession.default_merchant_uuid &&
      merchantUuids.includes(groupSession.default_merchant_uuid)
    ) {
      return groupSession.default_merchant_uuid
    }

    return merchantUuids[0] || groupSession.default_merchant_uuid || undefined
    // A empresa inicial é decidida uma vez, na abertura do formulário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [companyUuid, setCompanyUuid] = useState<string | undefined>(
    initialCompany
  )

  useActiveMerchant(enabled && groupSession ? companyUuid : undefined)

  return {
    companyUuid: enabled && groupSession ? companyUuid : undefined,
    setCompanyUuid
  }
}

export default useFormCompany
